import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";

import { api } from "../lib/api.ts";

interface Digest {
	id: string;
	ts: string;
	summary: string;
	body: string;
	inputTokens: number;
	outputTokens: number;
	estCostUsd: number;
}

export function Digests() {
	const [digests, setDigests] = useState<Digest[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [generating, setGenerating] = useState(false);
	const [expandedId, setExpandedId] = useState<string | null>(null);

	async function load() {
		try {
			const res = await api.listDigests();
			setDigests(res.digests);
			if (res.digests.length > 0) setExpandedId(res.digests[0]!.id);
		} catch (err) {
			setError((err as Error).message);
		}
	}

	useEffect(() => {
		void load();
	}, []);

	async function runNow() {
		setGenerating(true);
		setError(null);
		try {
			await api.runDigestNow();
			await load();
		} catch (err) {
			setError((err as Error).message);
		} finally {
			setGenerating(false);
		}
	}

	return (
		<div className="px-6 py-8">
			<div className="flex items-baseline justify-between mb-6">
				<div>
					<h1 className="text-xl font-semibold">Morning digests</h1>
					<p className="text-sm text-zinc-500 mt-1">
						Auto-generated daily at 07:00 UTC. Click any digest to expand.
					</p>
				</div>
				<button
					type="button"
					onClick={runNow}
					disabled={generating}
					className="px-3 py-1.5 text-sm rounded-md bg-accent-600 hover:bg-accent-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-white"
				>
					{generating ? "Generating…" : "Generate now"}
				</button>
			</div>

			{error && (
				<div className="p-3 border border-red-500/30 rounded bg-red-500/10 text-sm text-red-300 mb-4">
					{error}
				</div>
			)}

			{digests !== null && digests.length === 0 && !error && (
				<div className="text-center text-zinc-500 mt-16 text-sm">
					No digests yet. Click "Generate now" to create your first one.
				</div>
			)}

			<div className="space-y-3">
				{digests?.map((d) => (
					<DigestCard
						key={d.id}
						d={d}
						expanded={expandedId === d.id}
						onToggle={() => setExpandedId(expandedId === d.id ? null : d.id)}
					/>
				))}
			</div>
		</div>
	);
}

function DigestCard({ d, expanded, onToggle }: { d: Digest; expanded: boolean; onToggle: () => void }) {
	const date = new Date(d.ts);
	return (
		<div className="border border-zinc-800 rounded-lg bg-zinc-900/40 overflow-hidden">
			<button
				type="button"
				onClick={onToggle}
				className="w-full text-left px-4 py-3 hover:bg-zinc-800/40"
			>
				<div className="flex items-baseline justify-between gap-3">
					<div>
						<div className="text-sm font-semibold text-zinc-100">
							{date.toLocaleDateString("en-US", {
								weekday: "short",
								month: "short",
								day: "numeric",
							})}{" "}
							digest
						</div>
						<div className="text-xs text-zinc-500 mt-0.5">
							{date.toLocaleTimeString()} · {d.inputTokens}/{d.outputTokens} tokens ·{" "}
							${d.estCostUsd.toFixed(4)}
						</div>
					</div>
					<span className="text-zinc-600 text-xs">{expanded ? "▼" : "▶"}</span>
				</div>
			</button>
			{expanded && (
				<div className="px-4 pb-4 border-t border-zinc-800/60">
					<div className="prose prose-invert prose-sm max-w-none pt-3">
						<ReactMarkdown>{d.body || "(no content)"}</ReactMarkdown>
					</div>
				</div>
			)}
		</div>
	);
}
